// timeFields — one home for every time/date/duration INPUT widget and every
// time-of-day DISPLAY helper, so the app reads times one consistent way:
// 12-hour clock, in the venue's timezone, with an explicit zone suffix.
//
// Widgets (controlled, presentation-only — caller owns persistence):
//   <TimeField value onChange format="HH:MM"|"HH:MM:SS" />   native time picker
//   <DateField value onChange />                              native date picker
//   <DateTimeField value onChange tz />                       datetime-local + tz caption
//   <DurationField seconds onChange relLabel />               number + unit, stores seconds
//
// Display helpers (read-only):
//   formatClock(iso, tz)      → "11:30 PM EDT"  (time of day only)
//   formatEventTime(iso, tz)  → "Fri, Aug 14 · 11:30 PM EDT"
//   formatStamp(iso)          → locale date+time for record metadata (created/synced)
//
// No date library — native inputs + Intl only, matching the rest of the app.

import { useState } from 'react';

/* eslint-disable react-refresh/only-export-components */

// ── display helpers ─────────────────────────────────────────────────────────

// Time-of-day in the venue tz with a short zone name ("11:30 PM EDT"). The
// zone suffix is always shown so a time is never ambiguous across surfaces.
export function formatClock(iso, tz) {
  if (!iso) return '';
  const opts = { hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' };
  try {
    return new Date(iso).toLocaleTimeString('en-US', { ...opts, timeZone: tz || 'UTC' });
  } catch {
    return new Date(iso).toLocaleTimeString('en-US', { ...opts, timeZone: 'UTC' });
  }
}

// Compact time of day (no zone suffix) for tight spaces like calendar chips.
export function formatClockShort(iso, tz) {
  if (!iso) return '';
  const opts = { hour: 'numeric', minute: '2-digit', hour12: true };
  try {
    return new Date(iso).toLocaleTimeString('en-US', { ...opts, timeZone: tz || 'UTC' });
  } catch {
    return new Date(iso).toLocaleTimeString('en-US', { ...opts, timeZone: 'UTC' });
  }
}

// Full weekday + date + time in the venue tz.
export function formatEventTime(iso, tz) {
  if (!iso) return '';
  const opts = {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  };
  try {
    const d = new Date(iso).toLocaleString('en-US', { ...opts, timeZone: tz || 'UTC' });
    return d.replace(', ', ', ').replace(/, (\d)/, ' · $1'); // "Fri, Aug 14, 11:30 PM" → "Fri, Aug 14 · 11:30 PM"
  } catch {
    return new Date(iso).toLocaleString('en-US', { ...opts, timeZone: 'UTC' });
  }
}

// Record metadata timestamps (created/synced/pushed/expires/last-used). Pinned
// to the VENUE timezone (pass tz) with the zone abbrev shown (e.g. "…EDT") so
// it's unambiguous. Falls back to UTC if tz omitted/invalid — never the
// browser's zone, which varies by viewer.
export function formatStamp(iso, tz = 'UTC') {
  if (!iso) return '—';
  const opts = {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short',
  };
  try { return new Date(iso).toLocaleString('en-US', { ...opts, timeZone: tz }); }
  catch {
    try { return new Date(iso).toLocaleString('en-US', { ...opts, timeZone: 'UTC' }); }
    catch { return String(iso); }
  }
}

// ── timezone-aware datetime-local conversion ────────────────────────────────
// datetime-local inputs are naive wall-clock strings. To edit an event's
// doors/end in the *venue's* timezone (not the viewer's browser tz), convert
// the stored instant to the venue's wall clock for the input, and back to an
// instant on save. A no-edit round-trip is exact (same tz both ways).

function tzParts(date, tz) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz || 'UTC', hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = {};
  for (const { type, value } of fmt.formatToParts(date)) p[type] = value;
  return {
    y: +p.year, mo: +p.month, d: +p.day,
    h: +(p.hour === '24' ? '0' : p.hour), mi: +p.minute, s: +p.second,
  };
}

function tzOffsetMs(utcMs, tz) {
  const { y, mo, d, h, mi, s } = tzParts(new Date(utcMs), tz);
  return Date.UTC(y, mo - 1, d, h, mi, s) - utcMs;
}

// ISO instant → "YYYY-MM-DDTHH:MM" wall clock in tz (for datetime-local).
export function isoToZonedInput(iso, tz) {
  if (!iso) return '';
  try {
    const { y, mo, d, h, mi } = tzParts(new Date(iso), tz || 'UTC');
    const pad = (n) => String(n).padStart(2, '0');
    return `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}`;
  } catch { return ''; }
}

// "YYYY-MM-DDTHH:MM" wall clock in tz → ISO instant.
export function zonedInputToIso(localStr, tz) {
  if (!localStr) return null;
  const [datePart, timePart = ''] = String(localStr).split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  if ([y, mo, d, h, mi].some((n) => Number.isNaN(n))) return null;
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  let instant = utcGuess - tzOffsetMs(utcGuess, tz || 'UTC');
  const refined = tzOffsetMs(instant, tz || 'UTC');
  instant = utcGuess - refined; // one refine pass handles DST boundaries
  return new Date(instant).toISOString();
}

// ── duration helpers ────────────────────────────────────────────────────────

const UNIT_SECS = { sec: 1, min: 60, hr: 3600, day: 86400 };
const UNIT_LABEL = { sec: 'sec', min: 'min', hr: 'hr', day: 'day' };

function pickUnit(seconds) {
  if (seconds == null || seconds === '') return { unit: 'hr', num: '' };
  const n = Number(seconds);
  if (Number.isNaN(n) || n === 0) return { unit: 'hr', num: n === 0 ? 0 : '' };
  for (const u of ['day', 'hr', 'min']) {
    if (n % UNIT_SECS[u] === 0) return { unit: u, num: n / UNIT_SECS[u] };
  }
  return { unit: 'sec', num: n };
}

export function humanizeDuration(seconds) {
  if (seconds == null || seconds === '') return '';
  const n = Number(seconds);
  if (Number.isNaN(n)) return '';
  if (n === 0) return '0 minutes';
  const { unit, num } = pickUnit(n);
  const word = { sec: 'second', min: 'minute', hr: 'hour', day: 'day' }[unit];
  return `${num} ${word}${num === 1 ? '' : 's'}`;
}

// ── input widgets ───────────────────────────────────────────────────────────

const INPUT_CLS = 'px-2 py-1 border border-input rounded-md bg-background text-xs';

// Native clock picker. `format` controls whether seconds round-trip.
export function TimeField({ value, onChange, format = 'HH:MM', className, disabled }) {
  const withSeconds = format === 'HH:MM:SS';
  const v = (value || '').slice(0, withSeconds ? 8 : 5);
  return (
    <input
      type="time"
      step={withSeconds ? 1 : 60}
      value={v}
      disabled={disabled}
      onChange={(e) => {
        let s = e.target.value;
        if (withSeconds && s && s.length === 5) s = `${s}:00`;
        onChange(s || null);
      }}
      className={className || INPUT_CLS}
    />
  );
}

export function DateField({ value, onChange, className, disabled }) {
  const v = (value || '').slice(0, 10);
  return (
    <input
      type="date"
      value={v}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
      className={className || INPUT_CLS}
    />
  );
}

// datetime-local with a visible timezone caption, so ops always know which
// zone they are typing in (fixes the silent UTC-vs-venue-tz trap).
export function DateTimeField({ value, onChange, tz, className, disabled, captionClass }) {
  // datetime-local needs "YYYY-MM-DDTHH:MM"; trim any ISO seconds / Z.
  const v = value && String(value).includes('T') ? String(value).slice(0, 16) : (value || '');
  return (
    <div>
      <input
        type="datetime-local"
        value={v}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || null)}
        className={className || INPUT_CLS}
      />
      <TzCaption tz={tz} className={captionClass} />
    </div>
  );
}

export function TzCaption({ tz, className }) {
  return (
    <div className={className || 'text-[10px] text-muted-foreground mt-0.5'}>
      times in venue local time{tz ? ` · ${tz}` : ' · UTC'}
    </div>
  );
}

// Duration entered as a number + unit; stores raw seconds underneath so no
// backend shape changes. Shows a plain-English echo of the stored value.
export function DurationField({ seconds, onChange, disabled, relLabel = '' }) {
  const [unit, setUnit] = useState(() => pickUnit(seconds).unit);
  const num = seconds == null || seconds === ''
    ? ''
    : Math.round((Number(seconds) / UNIT_SECS[unit]) * 100) / 100;
  const emit = (n, u) => {
    if (n === '' || n == null) { onChange(null); return; }
    const parsed = Number(n);
    onChange(Number.isNaN(parsed) ? null : Math.round(parsed * UNIT_SECS[u]));
  };
  const echo = seconds == null || seconds === '' ? '' : `= ${humanizeDuration(seconds)}${relLabel ? ` ${relLabel}` : ''}`;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <input
        type="number"
        min="0"
        value={num}
        disabled={disabled}
        onChange={(e) => emit(e.target.value, unit)}
        className="w-20 px-2 py-1 border border-input rounded-md bg-background text-xs text-right"
      />
      <select
        value={unit}
        disabled={disabled}
        onChange={(e) => { setUnit(e.target.value); emit(num, e.target.value); }}
        className="px-1 py-1 border border-input rounded-md bg-background text-xs"
      >
        {Object.keys(UNIT_SECS).map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
      </select>
      {echo && <span className="text-[10px] text-muted-foreground">{echo}</span>}
    </div>
  );
}
